import "@/App.css";
import {
  BrowserRouter,
  Routes,
  Route,
  Outlet,
  useLocation,
} from "react-router-dom";
import { useEffect } from "react";
import { Toaster } from "sonner";
import { AuthProvider } from "@/context/AuthContext";
import ProtectedRoute from "@/components/ProtectedRoute";
import Header from "@/components/layout/Header";
import Footer from "@/components/layout/Footer";
import AnalyticsRouteTracker from "@/components/AnalyticsRouteTracker";
import Seo from "@/components/Seo";
import BuilderTheme from "@/components/BuilderTheme";
import NativePageLayout from "@/components/NativePageLayout";

import Home from "@/pages/Home";
import About from "@/pages/About";
import GlobalNetwork, { GlobalLocationDetail } from "@/pages/GlobalNetwork";
import Media from "@/pages/Media";
import Contact from "@/pages/Contact";
import CmsPage from "@/pages/CmsPage";
import DetailPage from "@/pages/DetailPage";
import Dashboard from "@/pages/Dashboard";
import Admin from "@/pages/Admin";
import {
  NewsPage,
  BlogsPage,
  ArticlesPage,
  OrganizationsPage,
  OpportunitiesPage,
  EventsPage,
  InitiativesPage,
} from "@/pages/Listings";
import {
  Login,
  SignUp,
  VerifyEmail,
  ForgotPassword,
  ResetPassword,
} from "@/pages/Auth";
import {
  HeritageDirectory,
  HeritageCommunity,
  HeritagePlace,
} from "@/pages/Heritage";
import { LegalPage, NotFound } from "@/pages/Legal";

function ScrollTop() {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  return null;
}

function PublicLayout() {
  return (
    <>
      <Header />
      <Outlet />
      <Footer />
    </>
  );
}
const Native = ({ slug, children }) => <NativePageLayout slug={slug}>{children}</NativePageLayout>;

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <ScrollTop />
          <AnalyticsRouteTracker />
          <Seo />
          <BuilderTheme />
          <Toaster theme="dark" position="top-right" richColors />
          <Routes>
            <Route element={<PublicLayout />}>
              <Route path="/" element={<Native slug="home"><Home /></Native>} />
              <Route path="/about" element={<Native slug="about"><About /></Native>} />
              <Route path="/global-network" element={<Native slug="global-network"><GlobalNetwork /></Native>} />
              <Route
                path="/global-network/:slug"
                element={<GlobalLocationDetail />}
              />
              <Route path="/heritage" element={<Native slug="heritage"><HeritageDirectory /></Native>} />
              <Route
                path="/heritage/places/:slug"
                element={<HeritagePlace />}
              />
              <Route path="/heritage/:slug" element={<HeritageCommunity />} />
              <Route path="/news" element={<Native slug="news"><NewsPage /></Native>} />
              <Route path="/news/:slug" element={<DetailPage type="news" />} />
              <Route path="/articles" element={<Native slug="articles"><ArticlesPage /></Native>} />
              <Route
                path="/articles/:slug"
                element={<DetailPage type="article" />}
              />
              <Route path="/blogs" element={<Native slug="blogs"><BlogsPage /></Native>} />
              <Route path="/blog/:slug" element={<DetailPage type="blog" />} />
              <Route path="/blogs/:slug" element={<DetailPage type="blog" />} />
              <Route path="/organizations" element={<Native slug="organizations"><OrganizationsPage /></Native>} />
              <Route
                path="/organizations/:slug"
                element={<DetailPage type="organizations" />}
              />
              <Route path="/opportunities" element={<Native slug="opportunities"><OpportunitiesPage /></Native>} />
              <Route
                path="/opportunities/:slug"
                element={<DetailPage type="opportunities" />}
              />
              <Route path="/events" element={<Native slug="events"><EventsPage /></Native>} />
              <Route
                path="/events/:slug"
                element={<DetailPage type="events" />}
              />
              <Route path="/initiatives" element={<Native slug="initiatives"><InitiativesPage /></Native>} />
              <Route
                path="/initiatives/:slug"
                element={<DetailPage type="initiatives" />}
              />
              <Route path="/media" element={<Native slug="media"><Media /></Native>} />
              <Route path="/contact" element={<Native slug="contact"><Contact /></Native>} />
              <Route path="/privacy" element={<LegalPage type="privacy" />} />
              <Route path="/terms" element={<LegalPage type="terms" />} />
              <Route path="/cookies" element={<LegalPage type="cookies" />} />
              <Route path="/page/:slug" element={<CmsPage />} />
              <Route path="/:slug" element={<CmsPage />} />
              <Route path="*" element={<NotFound />} />
            </Route>

            <Route path="/login" element={<Login />} />
            <Route path="/signup" element={<SignUp />} />
            <Route path="/verify-email" element={<VerifyEmail />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />

            <Route
              path="/dashboard"
              element={
                <ProtectedRoute>
                  <Dashboard />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin"
              element={
                <ProtectedRoute staffOnly>
                  <Admin />
                </ProtectedRoute>
              }
            />
          </Routes>
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
